import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-root-toast';
import { View } from 'react-native';
import { compose } from 'redux';
import React, { useCallback, useMemo, useState } from 'react';

import useConfirmStockLevels from '../../../common/utils/serviceDocket/useConfirmStockLevels';
import { nativePlanogramVersions } from '../../../common/utils/vending-settings';
import { useUserContext } from '../../../common/utils/context';
import { useBooleanState } from '../../../common/utils/hooks';

import { CONFIRM_OFFLINE_MACHINE_VALUES } from '../../../common/graphql/serviceDocket';
import { Heading, Dialog, Text } from '../../components';
import { PrimaryButton, Screen, StatusBadge } from '../../design/components';
import { ConfirmStockLevelsContext } from '../../utils/context';
import { defaultConfirmStockLevelsSort, sortPickListItems } from '../../utils/sortMethods';
import { DetailContainer } from '../../containers';
import { useRefetchOnFocus } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineMutation, useOfflineQueue } from '../../offline';

import PlanogramItemExpiryDatePicker from '../planogram/PlanogramItemExpiryDatePicker';
import ReportIncorrectStockSettingDialog from '../planogram/ReportIncorrectStockSettingDialog';
import { MachineValuesEdit, PlanogramDetail } from '../planogram';
import PlanogramSettingsButton from '../planogram/PlanogramSettingsButton';
import { isMyDayTraining } from '../myDay/myDayTraining';

export const ValuesConfirmationStatus = ({ isConfirmed, pendingSync }) => {
	const styles = useThemedStyles(createStyles);

	return (
		<View style={styles.status}>
			<Text style={styles.statusLabel}>Confirmation status</Text>
			<View style={styles.statusBadges}>
				{pendingSync && (
					<StatusBadge label="Sync pending" tone="warning" size="small" />
				)}
				<StatusBadge
					label={isConfirmed ? 'Confirmed' : 'Not confirmed'}
					tone={isConfirmed ? 'success' : 'warning'}
					size="small"
				/>
			</View>
		</View>
	);
};

const ConfirmMachineValues = ({
	data: docket,
	id: docketId,
	refetch,
	setAnyUnsavedChanges,
	setConfimValues,
	anyUnsavedChanges,
	trainingContext,
	backLabel,
	onBack,
	banner,
}) => {
	const styles = useThemedStyles(createStyles);
	const [isSettingsDialogOpen, toggleSettingsDialog] = useBooleanState(false);
	const { user, tenant, tenantSettings } = useUserContext();
	const { isOnline, queue } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const [reportTargetId, setReportTargetId] = useState(null);

	const {
		confirmStockLevelsItems,
		loading,
		confirmStockLevelsContextValues,
		machineUsesIngredients,
		isMarket,
		machineName,
		onPressConfirmStockLevels,

		onClickPlanogramItem,
		isRefreshing,
		editedPickListItemId,
		setEditedPickListItemId,
		expiryDateItemKey,
		setExpiryDateItemKey,
		onClickExpiryDateButton,
		openEditDialog,
		setOpenEditDialog,
		setExpiryDateSelectorDate,
		expiryDateSelectorDate,
		changeStockItem,
		reportIncorrectStockSetting,
		isStockSettingReported,
		reportingIncorrectStockSetting,
	} = useConfirmStockLevels({
		confirmStockLevelsMutation: CONFIRM_OFFLINE_MACHINE_VALUES,
		docket,
		docketId,
		refetch,
		setAnyUnsavedChanges,
		setConfirmStockLevels: setConfimValues,
		isFullByDefault: true,
		tenantSettings,
		timezone: tenant?.timezone,
		// Values confirmations queue offline like stock level confirmations do.
		mutationHook: useOfflineMutation,
		mutationHookOptions: {
			configKey: 'confirmMachineValues',
			disableOffline: isTraining,
		},
		reportMutationHook: useOfflineMutation,
		reportMutationHookOptions: {
			configKey: 'reportIncorrectStockSetting',
			disableOffline: isTraining,
		},
		onReportIncorrectStockSetting: useCallback(itemId => setReportTargetId(itemId), []),
		adminTraining: isTraining,
	});
	const reportTarget = reportTargetId
		? confirmStockLevelsItems.find(item => item.id === reportTargetId)
		: null;
	const closeReportDialog = useCallback(() => setReportTargetId(null), []);
	const submitReportIncorrectSetting = useCallback(async ({ setting, suggestedValue, detail }) => {
		const result = await reportIncorrectStockSetting({
			itemId: reportTargetId,
			setting,
			suggestedValue,
			detail,
		});
		setReportTargetId(null);
		if (result?.offline) {
			Toast.show('Report queued; your manager will be notified when back online');
		}
		else {
			Toast.show('Your manager has been notified');
		}
	}, [reportIncorrectStockSetting, reportTargetId]);
	const hasPendingValuesSync = useMemo(() => (scopedQueue || []).some(entry => (
		entry.configKey === 'confirmMachineValues' &&
		entry.meta?.serviceDocketId === docketId
	)), [scopedQueue, docketId]);
	useRefetchOnFocus(refetch, {
		enabled: (
			!anyUnsavedChanges &&
			!loading &&
			!hasPendingValuesSync &&
			!openEditDialog &&
			!expiryDateItemKey
		),
	});

	const navigation = useNavigation();


	const {
		settings: {
			confirmStockLevelsSort,
			confirmStockLevelsHideImages,
			confirmStockLevelsHideHealthCompliance,
			confirmStockLevelsShowSalesLastXDays,
		},
	} = user;

	const onPressConfirm = async () => {
		try {
			const result = await onPressConfirmStockLevels();
			if (result?.offline) {
				Toast.show('Confirmation queued; will sync when back online');
			}
			navigation.goBack();
		}
		catch (e) {
			Toast.show(e?.message || 'Error when confirming values');
		}
	};

	return (
		<View style={styles.container}>
			<Screen
				scroll={false}
				backLabel={backLabel}
				onBack={onBack}
				banner={banner}
				headerRight={(
					<View style={styles.headerActions}>
						<ServiceNoteButton />
						<PlanogramSettingsButton onPress={toggleSettingsDialog} accessibilityLabel="Machine value settings" />
					</View>
				)}
			>
				{ loading && (
					<View
						style={styles.loadingOverlay}
					/>
				)}
				<KeyboardAwareScrollView
					scrollIndicatorInsets={{ right: 1 }}
				>
					<View style={styles.top}>
						<Heading
							heading="Confirm values"
							subHeading={machineName}
						/>
						<ValuesConfirmationStatus isConfirmed={docket.isRestocked} pendingSync={hasPendingValuesSync} />
					</View>
					<View style={styles.list}>
						<ConfirmStockLevelsContext.Provider
							value={confirmStockLevelsContextValues}
						>
							<PlanogramDetail
								items={confirmStockLevelsItems}
								isConfirmValues={true}
								version={nativePlanogramVersions.CONFIRM_OFFLINE_VALUES}
								// Default planogram filters
								defaultSortMethod={confirmStockLevelsSort || defaultConfirmStockLevelsSort}
								sortFunction={sortPickListItems}
								defaultHideImages={confirmStockLevelsHideImages}
								defaultHideHealthCompliance={confirmStockLevelsHideHealthCompliance}
								machineUsesIngredients={machineUsesIngredients}
								defaultShowSalesLastXDays={confirmStockLevelsShowSalesLastXDays}
								defaultShowExpiryDateButtons={true}
								isMarket={isMarket}
								planogramItemGroups={docket.machine.planogram.planogramItemGroups}
								isSettingsDialogOpen={isSettingsDialogOpen}
								toggleSettingsDialog={toggleSettingsDialog}
								onClickItem={onClickPlanogramItem}
							/>
						</ConfirmStockLevelsContext.Provider>
					</View>
					{/* After the last item, not pinned, so drivers scroll through every item before confirming. */}
					<View style={styles.footer}>
						<PrimaryButton
							label="Confirm values"
							disabled={loading || (isTraining && !isOnline)}
							loading={loading}
							onPress={onPressConfirm}
						/>
					</View>
				</KeyboardAwareScrollView>
			</Screen>
			<ReportIncorrectStockSettingDialog
				isOpen={Boolean(reportTargetId && reportTarget)}
				planogramItem={reportTarget}
				showCapacity={Boolean(tenantSettings.enablePlanogramItemCapacity)}
				reportedSettings={{
					PARLEVEL: isStockSettingReported(reportTargetId, 'PARLEVEL'),
					CAPACITY: isStockSettingReported(reportTargetId, 'CAPACITY'),
				}}
				submitting={reportingIncorrectStockSetting}
				onClose={closeReportDialog}
				onSubmit={submitReportIncorrectSetting}
			/>
			<Dialog
				isOpen={editedPickListItemId && !expiryDateItemKey && openEditDialog}
				onClose={() => {
					setEditedPickListItemId(null);
					setOpenEditDialog(false);
				}}
			>
				{ editedPickListItemId && !expiryDateItemKey && openEditDialog && (
					<MachineValuesEdit
						isRefreshing={isRefreshing}
						isMarket={isMarket}
						planogramItem={confirmStockLevelsItems.find(i => i.id === editedPickListItemId)}
						setPlanogramItemValue={changeStockItem}
						defaultShowSalesLastXDays={confirmStockLevelsShowSalesLastXDays}
						pos={docket.machine}
					/>
				) }
			</Dialog>
			{
				(expiryDateItemKey && editedPickListItemId && tenantSettings.useExpiryDates) && (
					<PlanogramItemExpiryDatePicker
						planogramItem={confirmStockLevelsItems.find(i => i.id === editedPickListItemId)}
						setPlanogramItemValue={changeStockItem}
						expiryDateItemKey={expiryDateItemKey}
						setExpiryDateItemKey={setExpiryDateItemKey}
						setExpiryDateSelectorDate={setExpiryDateSelectorDate}
						expiryDateSelectorDate={expiryDateSelectorDate}
						onClickExpiryDateButton={onClickExpiryDateButton}
					/>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	headerActions: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
	},
	container: {
		backgroundColor: colors.canvas,
		flex: 1,
	},
	loadingOverlay: {
		position: 'absolute',
		backgroundColor: colors.canvas,
		opacity: 0.6,
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		zIndex: 999,
	},
	top: {
		paddingHorizontal: space.gutter,
		gap: space.md,
	},
	list: {
		paddingBottom: space.lg,
	},
	footer: {
		paddingHorizontal: space.gutter,
		paddingBottom: space.xl,
	},
	status: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.sm,
		paddingBottom: space.xs,
	},
	statusLabel: {
		...type.overline,
		color: colors.textSecondary,
	},
	statusBadges: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		gap: space.xs,
	},
});

export default compose(DetailContainer)(ConfirmMachineValues);
